import RedColor from "@material-ui/core/colors/red"
import createBreakpoints from "@material-ui/core/styles/createBreakpoints"
import chroma from "chroma-js"
import { isHexColorValid } from "../../utils/general"
import { pipe } from "ramda"
import { createShadow } from "./utils"
import { Maybe } from "../../graphql/generated-types"
import createMuiTheme, { Theme, ThemeOptions } from "@material-ui/core/styles/createMuiTheme"

const DefaultPrimaryColor = "#50E3C2"

const muiTheme = (merchantColor?: Maybe<string>): ThemeOptions => {
    const primaryColorHex = isHexColorValid(merchantColor) ? chroma(merchantColor).hex() : DefaultPrimaryColor
    const primaryLightColor = isHexColorValid(merchantColor) ? chroma(merchantColor).brighten(0.75).hex() : "#8afff5"
    const primaryDarkColor = isHexColorValid(merchantColor) ? chroma(merchantColor).darken(0.75).hex() : "#00b092"

    const inputBorderRadius = {
        borderTopLeftRadius: "10px",
        borderTopRightRadius: "10px",
        borderBottomLeftRadius: "10px",
        borderBottomRightRadius: "10px",
    }

    // @ts-ignore
    return {
        breakpoints: createBreakpoints({}),
        typography: {
            // htmlFontSize: 14,
            fontFamily: ['"Nunito Sans"', '"Helvetica"', '"Arial"', "sans-serif"].join(","),
            fontSize: 14,
            h1: { fontWeight: "bold" },
            h2: { fontWeight: "bold" },
            h3: { fontWeight: "bold" },
            h4: { fontWeight: "bold" },
            h5: { fontWeight: "bold" },
            h6: { fontWeight: "bold", lineHeight: 1.3 },
            subtitle1: {
                fontSize: "1.0rem",
            },
            subtitle2: {
                fontSize: "1.1rem",
            },
            body1: {
                fontWeight: 400,
            },
            body2: {
                fontWeight: 500,
            },
            button: {
                fontSize: "0.875rem",
                fontWeight: 700,
            },
        },
        palette: {
            primary: {
                light: primaryLightColor,
                main: primaryColorHex,
                dark: primaryDarkColor,
            },
            secondary: {
                light: "#ff7539",
                main: "#FF3D00",
                dark: "#c30000",
            },
            text: {
                primary: "rgba(0, 0, 20, 0.72)",
                secondary: "rgba(90, 90, 100, 0.74)",
                disabled: "rgba(90, 90, 100, 0.58)",
                hint: "rgba(90, 90, 100, 0.48)",
            },
            background: {
                default: "#FFF",
            },
        },
        overrides: {
            MuiButton: {
                containedPrimary: {
                    color: "white",
                    "&$disabled": {
                        backgroundColor: "#EFEFEF!important",
                        color: "rgba(0, 0, 0, 0.35)!important",
                    },
                },
                outlinedPrimary: {
                    textTransform: "none",
                    backgroundColor: chroma(primaryColorHex).alpha(0.1).css() + "!important",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.03), 1px 1px 9px rgba(0,0,0,0.04)!important",
                    "&:disabled": {
                        backgroundColor: "#EEE!important",
                    },
                    "&:hover": {
                        backgroundColor: chroma(primaryColorHex).alpha(0.1).css(),
                    },
                    "&:focus": {
                        backgroundColor: chroma(primaryColorHex).alpha(0.1).css(),
                    },
                },
                sizeLarge: {
                    fontSize: "1rem",
                },
                root: {
                    ...inputBorderRadius,
                    textTransform: "none",
                    "&$disabled": {
                        color: "rgba(0, 0, 0, 0.65)!important",
                        boxShadow: "none!important",
                    },
                },
            },
            MuiInputBase: {
                input: {
                    outline: "none!important",
                    border: "none!important",
                    boxShadow: "none!important",
                },
            },
            MuiInput: {
                input: {
                    ...inputBorderRadius,
                },
                underline: {
                    "&::before": {
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    },
                },
            },
            MuiInputLabel: {
                filled: {
                    transform: "translate(12px, 18px) scale(1)",
                },
                shrink: {
                    transform: "translate(12px, 8px) scale(0.75)!important",
                },
            },
            MuiAppBar: {
                colorDefault: {
                    backgroundColor: "white",
                    boxShadow: createShadow(0, 1, 3, 0, 0, 1, 1, 0, 0, 2, 1, -1),
                },
            },
            MuiBottomNavigation: {
                root: {
                    height: "60px",
                    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                },
            },
            MuiBottomNavigationAction: {
                root: {
                    paddingTop: "8px!important",
                },
                label: {
                    fontWeight: "bold",
                    fontSize: "0.85rem!important",
                    transition: "none!important",
                },
            },
            MuiChip: {
                root: {
                    backgroundColor: "rgba(0, 0, 0, 0.07)",
                    height: "18px",
                    fontSize: "0.75rem",
                },
            },
            MuiTabs: {
                scroller: {
                    "-ms-overflow-style": "none", // IE 10+
                    overflow: "-moz-scrollbars-none", // Firefox
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                },
                root: {
                    minHeight: "42px",
                },
            },
            MuiTab: {
                root: {
                    minHeight: "42px",
                    fontSize: "0.95rem!important",
                    textTransform: "none",
                },
            },
            MuiListItemText: {
                primary: {
                    fontWeight: "bold",
                },
            },
            MuiButtonBase: {
                root: {
                    "&$disabled": {
                        backgroundColor: "#EFEFEF",
                    },
                },
            },
            MuiToolbar: {
                root: {
                    minHeight: "52px!important",
                    padding: "0 0.5rem!important",
                },
            },
            MuiIconButton: {
                root: {
                    padding: "8px 12px",
                },
            },
            MuiListItem: {
                root: {
                    "&:focus": {
                        background: "rgba(0, 0, 0, 0.04)!important",
                    },
                    "&:hover": {
                        background: "rgba(0, 0, 0, 0.04)!important",
                    },
                },
            },
            MuiOutlinedInput: {
                input: {
                    ...inputBorderRadius,
                    backgroundColor: "transparent!important",
                },
            },
            MuiFilledInput: {
                root: {
                    ...inputBorderRadius,
                    color: "rgba(0, 0, 0, 0.8)!important",
                    backgroundColor: "white!important",
                    border: "1px solid #00000015",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.02), 1px 1px 9px rgba(0,0,0,0.04)",
                    transition: "all 200ms ease-in-out",
                    "&:hover": {
                        boxShadow: "0 2px 7px rgba(0,0,0,0.04), 1px 1px 11px rgba(0,0,0,0.05)",
                        backgroundColor: "white!important",
                    },
                    "&$focused": {
                        backgroundColor: "white!important",
                        border: "1px solid " + primaryColorHex,
                        boxShadow: createShadow(0, 1, 3, 0, 0, 1, 1, 0, 0, 2, 1, -1),
                    },
                    "&::before": {
                        borderWidth: "0px!important",
                    },
                    "&::after": {
                        borderWidth: "0px!important",
                    },
                    "&$disabled": {
                        color: "rgba(0, 0, 0, 0.4)!important",
                        backgroundColor: "rgba(0, 0, 0, 0.03)!important",
                    },
                    "&$error": {
                        border: "1px solid " + RedColor["500"] + "!important",
                    },
                },
                input: {
                    ...inputBorderRadius,
                    padding: "22px 12px 7px",
                },
                multiline: {
                    padding: "22px 12px 7px",
                },
                inputMultiline: {
                    borderRadius: 0,
                },
            },
            MuiDialog: {
                paper: {
                    margin: "20px",
                    ...inputBorderRadius,
                },
            },
            MuiDialogTitle: {
                root: {
                    padding: "26px",
                },
            },
            MuiMenuItem: {
                root: {
                    height: "auto",
                },
            },
            MuiSelect: {
                selectMenu: {
                    paddingTop: "7px",
                },
                icon: {
                    right: "6px",
                },
            },
            MuiCheckbox: {
                root: {
                    "&$checked": {
                        color: "#757575",
                    },
                },
            },
            MuiRadio: {
                root: {
                    "&$checked": {
                        color: "#757575",
                    },
                },
            },
            MuiLinearProgress: {
                bar1Determinate: {
                    transition: "transform .6s ease-in-out",
                },
            },
            // @ts-ignore
            MuiPickersToolbarButton: {
                toolbarBtn: {
                    textAlign: "center",
                    width: "100%",
                },
            },
            MuiPickersCalendarHeader: {
                // @ts-ignore
                transitionContainer: {
                    "& p": {
                        fontWeight: "bold",
                    },
                },
            },
            MuiFormHelperText: {
                root: {
                    "&$error": {
                        whiteSpace: "pre-line",
                    },
                },
            },
        },
        shadows: [
            "none",
            createShadow(0, 1, 3, 0, 0, 1, 1, 0, 0, 2, 1, -1),
            createShadow(0, 1, 5, 0, 0, 2, 2, 0, 0, 3, 1, -2),
            createShadow(0, 1, 8, 0, 0, 3, 4, 0, 0, 3, 3, -2),
            createShadow(0, 2, 4, -1, 0, 4, 5, 0, 0, 1, 10, 0),
            createShadow(0, 3, 5, -1, 0, 5, 8, 0, 0, 1, 14, 0),
            createShadow(0, 3, 5, -1, 0, 6, 10, 0, 0, 1, 18, 0),
            createShadow(0, 4, 5, -2, 0, 7, 10, 1, 0, 2, 16, 1),
            createShadow(0, 5, 5, -3, 0, 8, 10, 1, 0, 3, 14, 2),
            createShadow(0, 5, 6, -3, 0, 9, 12, 1, 0, 3, 16, 2),
            createShadow(0, 6, 6, -3, 0, 10, 14, 1, 0, 4, 18, 3),
            createShadow(0, 6, 7, -4, 0, 11, 15, 1, 0, 4, 20, 3),
            createShadow(0, 7, 8, -4, 0, 12, 17, 2, 0, 5, 22, 4),
            createShadow(0, 7, 8, -4, 0, 13, 19, 2, 0, 5, 24, 4),
            createShadow(0, 7, 9, -4, 0, 14, 21, 2, 0, 5, 26, 4),
            createShadow(0, 8, 9, -5, 0, 15, 22, 2, 0, 6, 28, 5),
            createShadow(0, 8, 10, -5, 0, 16, 24, 2, 0, 6, 30, 5),
            createShadow(0, 8, 11, -5, 0, 17, 26, 2, 0, 6, 32, 5),
            createShadow(0, 9, 11, -5, 0, 18, 28, 2, 0, 7, 34, 6),
            createShadow(0, 9, 12, -6, 0, 19, 29, 2, 0, 7, 36, 6),
            createShadow(0, 10, 13, -6, 0, 20, 31, 3, 0, 8, 38, 7),
            createShadow(0, 10, 13, -6, 0, 21, 33, 3, 0, 8, 40, 7),
            createShadow(0, 10, 14, -6, 0, 22, 35, 3, 0, 8, 42, 7),
            createShadow(0, 11, 14, -7, 0, 23, 36, 3, 0, 9, 44, 8),
            "0px 11px 15px -7px rgba(0, 0, 0, 0.08), 0px 24px 38px 3px rgba(0, 0, 0, 0.04), " +
                "0px 9px 46px 8px rgba(0, 0, 0, 0.03)",
        ],
    }
}

export const getHighlightColor = (color) => {
    const c = chroma(color)
    return `rgba(${c.get("rgb.r")},` + `${c.get("rgb.g")},` + `${c.get("rgb.b")}, 0.1)`
}

export const getMuiTheme: (color?: Maybe<string>) => Theme = pipe(muiTheme, createMuiTheme)

export const chooseContrastColor = (baseColor: string, contrastOpt1: string, contrastOpt2: string) => {
    return chroma.contrast(baseColor, contrastOpt1) > 4.5 ? contrastOpt1 : contrastOpt2
}

export default muiTheme
